import { Button, Dialog, Field, Input, Select, useToast } from '@aurea/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useErrorText } from '../../lib/i18n';
import { fmtDate } from '../../lib/time';
import { isWritable, meQuery, useMe } from '../_app';

export const Route = createFileRoute('/_app/invites')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me.roles.includes('admin')) throw redirect({ to: '/' });
  },
  component: Invites,
});

function Invites() {
  const me = useMe();
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const invites = useQuery({ queryKey: ['invites'], queryFn: api.invites });
  const [creating, setCreating] = useState(false);
  const revoke = useMutation({
    mutationFn: api.revokeInvite,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites'] }),
  });

  const writable = isWritable(me);

  return (
    <>
      <header className="page-head">
        <h1>{t('invites.title')}</h1>
        <div className="flex-1" />
        {writable && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="i" />
            {t('invites.create')}
          </Button>
        )}
      </header>
      <section className="tray overflow-hidden">
        {invites.data?.length === 0 && <p className="p-6 text-sm text-ink-2">{t('invites.empty')}</p>}
        <ul className="rows px-4">
          {invites.data?.map((i) => (
            <li key={i.id} className={i.revoked ? 'opacity-55' : ''}>
              <div className="min-w-0 flex-1">
                <b className="block truncate font-semibold">{i.label}</b>
                <span className="text-xs text-ink-3">
                  {[
                    t(`roles.${i.role}`),
                    i.courseName && (i.groupName ? `${i.courseName}, ${i.groupName}` : i.courseName),
                    i.maxUses !== null
                      ? t('invites.usedOf', { used: i.usedCount, max: i.maxUses })
                      : t('invites.used', { used: i.usedCount }),
                    i.expiresAt &&
                      t('invites.until', { date: fmtDate(i.expiresAt, me.timezone, i18n.language) }),
                    i.revoked && t('invites.revoked'),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
              {!i.revoked && writable && (
                <Button variant="link" onClick={() => revoke.mutate(i.id)}>
                  {t('invites.revoke')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </section>
      {creating && <CreateInvite onClose={() => setCreating(false)} />}
    </>
  );
}

function CreateInvite({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const toast = useToast();
  const qc = useQueryClient();
  const groups = useQuery({ queryKey: ['groups'], queryFn: api.groups });
  const [url, setUrl] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: api.createInvite,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['invites'] });
      setUrl(r.url);
      navigator.clipboard?.writeText(r.url).catch(() => {});
    },
  });
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const num = (k: string) => (f.get(k) ? Number(f.get(k)) : undefined);
    create.mutate({
      label: String(f.get('label')).trim(),
      role: f.get('role') === 'teacher' ? 'teacher' : 'student',
      groupId: (f.get('groupId') as string) || undefined,
      expiresInDays: num('expires'),
      maxUses: num('maxUses'),
    });
  };
  return (
    <Dialog open onClose={onClose} title={t('invites.create')} strip="pink">
      {url ? (
        <div className="pb-6">
          <p className="text-sm text-ink-2">{t('invites.created')}</p>
          <div className="mt-3 flex gap-2">
            <Input readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
            <Button
              variant="ghost"
              onClick={() => {
                navigator.clipboard?.writeText(url);
                toast(t('common.copied'));
              }}
            >
              {t('common.copy')}
            </Button>
          </div>
          <Button className="mt-4" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      ) : (
        <form className="flex flex-col gap-4 pb-6" onSubmit={submit}>
          <Field label={t('invites.label')} hint={t('invites.labelHint')}>
            <Input name="label" required autoFocus />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('invites.role')}>
              <Select name="role" defaultValue="student">
                <option value="student">{t('roles.student')}</option>
                <option value="teacher">{t('roles.teacher')}</option>
              </Select>
            </Field>
            <Field label={t('invites.group')}>
              <Select name="groupId" defaultValue="">
                <option value="">{t('invites.noGroup')}</option>
                {groups.data?.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.courseName} · {g.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={`${t('invites.expires')} (${t('common.optional')})`}>
              <Input name="expires" type="number" min={1} max={365} />
            </Field>
            <Field label={`${t('invites.maxUses')} (${t('common.optional')})`}>
              <Input name="maxUses" type="number" min={1} />
            </Field>
          </div>
          {create.isError && <p className="text-sm text-danger">{errorText(create.error)}</p>}
          <div className="flex gap-3">
            <Button type="submit" loading={create.isPending}>
              {t('common.create')}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
